import React from 'react';
import { motion } from 'framer-motion';
import { Instagram, ExternalLink, Heart, MessageCircle } from 'lucide-react';
import ScrollReveal from './ScrollReveal';

const InstagramSection = () => {
  // Instagram handles
  const instagramAccounts = {
    jalsaRetreat: {
      handle: '@jalsaretreat',
      url: 'https://www.instagram.com/jalsaretreat/',
      name: 'Jalsa Retreat'
    },
    barLounge: {
      handle: '@101bhopal',
      url: 'https://www.instagram.com/101bhopal/',
      name: '101 Bar & Lounge'
    }
  };

  // Using actual property images for Instagram showcase
  const instagramPosts = [
    {
      id: 1,
      image: 'https://customer-assets.emergentagent.com/job_7f6b4518-3cb7-41cb-8ead-7fed5244d35b/artifacts/kkd2nxvn_SHS_2775-Pano.jpg',
      likes: '1.2K',
      comments: '48',
      caption: 'Lakeside serenity at its finest',
      account: 'jalsaRetreat'
    },
    {
      id: 2,
      image: 'https://customer-assets.emergentagent.com/job_7f6b4518-3cb7-41cb-8ead-7fed5244d35b/artifacts/na83rgs7_SHS_2463.jpg',
      likes: '892',
      comments: '35',
      caption: 'Your home away from home',
      account: 'jalsaRetreat'
    },
    {
      id: 3,
      image: 'https://customer-assets.emergentagent.com/job_7f6b4518-3cb7-41cb-8ead-7fed5244d35b/artifacts/z3jbql7q_Jalsa%20Inn%20Facade.png',
      likes: '2.1K',
      comments: '89',
      caption: 'Where luxury meets comfort',
      account: 'jalsaRetreat'
    },
    {
      id: 4,
      image: 'https://customer-assets.emergentagent.com/job_7f6b4518-3cb7-41cb-8ead-7fed5244d35b/artifacts/ffwcuz4x_WhatsApp%20Image%202026-02-14%20at%208.25.41%20PM.jpeg',
      likes: '756',
      comments: '42',
      caption: 'Crafted to perfection',
      account: 'barLounge'
    },
    {
      id: 5,
      image: 'https://customer-assets.emergentagent.com/job_7f6b4518-3cb7-41cb-8ead-7fed5244d35b/artifacts/mh703j8o_WhatsApp%20Image%202026-02-14%20at%208.25.42%20PM.jpeg',
      likes: '1.5K',
      comments: '67',
      caption: 'Nights to remember at 101',
      account: 'barLounge'
    },
    {
      id: 6,
      image: 'https://customer-assets.emergentagent.com/job_7f6b4518-3cb7-41cb-8ead-7fed5244d35b/artifacts/fntpl2na_WhatsApp%20Image%202026-02-14%20at%208.25.42%20PM%20%281%29.jpeg',
      likes: '1.8K',
      comments: '54',
      caption: 'Cheers to the good life',
      account: 'barLounge'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-navy-900 to-navy-800 overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <ScrollReveal direction="up">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-6">
              <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-400" />
              <Instagram className="mx-4 w-6 h-6 text-gold-400" />
              <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-400" />
            </div>
            
            <h2 className="text-4xl md:text-5xl font-serif text-white mb-4">
              Follow Our Journey
            </h2>
            
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Stay connected with us on Instagram for the latest updates, behind-the-scenes moments, and exclusive offers
            </p>
          </div>
        </ScrollReveal>

        {/* Instagram Grid */}
        <ScrollReveal direction="up" delay={0.2}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {instagramPosts.map((post, index) => (
              <motion.a
                key={post.id}
                href={instagramAccounts[post.account].url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="relative group aspect-square overflow-hidden rounded-lg"
              >
                {/* Post Image */}
                <img
                  src={post.image}
                  alt={post.caption}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-navy-900/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center">
                  {/* Engagement Stats */}
                  <div className="flex items-center space-x-4 text-white mb-2">
                    <span className="flex items-center space-x-1">
                      <Heart className="w-4 h-4 fill-white" />
                      <span className="text-sm font-semibold">{post.likes}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm font-semibold">{post.comments}</span>
                    </span>
                  </div>
                  
                  {/* Account Badge */}
                  <span className="text-gold-400 text-xs">
                    {instagramAccounts[post.account].handle}
                  </span>
                </div>

                {/* Instagram Icon Corner */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Instagram className="w-5 h-5 text-white" />
                </div>
              </motion.a>
            ))}
          </div>
        </ScrollReveal>

        {/* Follow Us Buttons */}
        <ScrollReveal direction="up" delay={0.3}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            {/* Jalsa Retreat */}
            <motion.a
              href={instagramAccounts.jalsaRetreat.url}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 rounded-full text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Instagram className="w-5 h-5" />
              <span>Follow {instagramAccounts.jalsaRetreat.handle}</span>
              <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.a>

            {/* 101 Bar & Lounge */}
            <motion.a
              href={instagramAccounts.barLounge.url}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 rounded-full text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Instagram className="w-5 h-5" />
              <span>Follow {instagramAccounts.barLounge.handle}</span>
              <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.a>
          </div>
        </ScrollReveal>

        {/* Hashtag */}
        <ScrollReveal direction="up" delay={0.4}>
          <p className="text-center text-gray-500 mt-8">
            Share your moments with us using <span className="text-gold-400 font-semibold">#JalsaRetreat</span> and <span className="text-gold-400 font-semibold">#101BarLounge</span>
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default InstagramSection;
